// Racket Lab — Create-ticket dialog (R4 data-quality hardening, Feb 2026).
//
// Improvements over the v1:
//   • Player picker: typeahead backed by /api/racket-lab/players/search,
//     debounced to 250ms, shows up to 10 matches with name + email +
//     profile tag. Free-text entry is rejected — the user MUST pick
//     someone from the suggestion list, otherwise the submit button stays
//     disabled. This eliminates the previous bug where any random string
//     could be submitted as a player_id.
//   • Sport: shadcn <Select> bound to the 6 backend Literal values
//     (tennis / padel / pickleball / squash / badminton / other).
//   • Racket model: client-side required + ≥2 char min (mirrors backend
//     normaliser). Inline rose-700 error + aria-invalid until valid.
//   • Inline errors per field. Submit button stays disabled until ALL
//     required fields are valid.
//
// Privacy: the search endpoint already strips sensitive fields (no
// password, no consent, no PII beyond name/email/profile/country).

import React, { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, X, CheckCircle2 } from "lucide-react";

const SPORTS = ["tennis", "padel", "pickleball", "squash", "badminton", "other"];

export default function TicketCreateDialog({ open, onOpenChange, onCreated }) {
  const { t } = useLang();

  // Player picker state.
  const [playerQuery, setPlayerQuery] = useState("");
  const [playerSuggestions, setPlayerSuggestions] = useState([]);
  const [playerSelected, setPlayerSelected] = useState(null);
  const [playerSearching, setPlayerSearching] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const debounceRef = useRef(null);

  // Other fields.
  const [racketModel, setRacketModel] = useState("");
  const [sport, setSport] = useState("tennis");
  const [notes, setNotes] = useState("");

  // Submit state.
  const [submitting, setSubmitting] = useState(false);
  const [racketTouched, setRacketTouched] = useState(false);

  const reset = () => {
    setPlayerQuery("");
    setPlayerSuggestions([]);
    setPlayerSelected(null);
    setPlayerOpen(false);
    setRacketModel("");
    setSport("tennis");
    setNotes("");
    setRacketTouched(false);
  };

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    if (playerSelected) return undefined; // already locked
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = playerQuery.trim();
    if (q.length < 2) {
      setPlayerSuggestions([]);
      setPlayerSearching(false);
      return undefined;
    }
    setPlayerSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/racket-lab/players/search?q=${encodeURIComponent(q)}`);
        setPlayerSuggestions(r.data?.items || []);
      } catch {
        setPlayerSuggestions([]);
      } finally {
        setPlayerSearching(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [playerQuery, playerSelected, open]);

  const racketValid = racketModel.trim().length >= 2;
  const racketError = racketTouched && !racketValid;
  const canSubmit = !!playerSelected && racketValid && !!sport && !submitting;

  const submit = async () => {
    setRacketTouched(true);
    if (!playerSelected) {
      toast.error(t("racketLab.tickets.createMissingPlayer"));
      return;
    }
    if (!racketValid) {
      toast.error(t("racketLab.tickets.createMissingRacket"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/racket-lab/service-tickets", {
        player_id: playerSelected.id,
        racket_model: racketModel.trim(),
        sport,
        notes: notes.trim() || null,
      });
      toast.success(t("racketLab.tickets.createOk"));
      reset();
      onCreated();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("racketLab.tickets.createFail")));
    } finally {
      setSubmitting(false);
    }
  };

  const clearPlayer = () => {
    setPlayerSelected(null);
    setPlayerQuery("");
    setPlayerSuggestions([]);
    setPlayerOpen(true);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        data-testid="racket-lab-tickets-create-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("racketLab.tickets.createTitle")}</DialogTitle>
          <DialogDescription className="text-xs">
            {t("racketLab.tickets.createDialogDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Player picker */}
          <div className="relative">
            <Label htmlFor="ticket-player-id">
              {t("racketLab.tickets.createPlayerLabel")}{" "}
              <span className="text-rose-600">*</span>
            </Label>

            {playerSelected ? (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm"
                data-testid="racket-lab-tickets-create-player-selected"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-emerald-900 truncate">
                      {playerSelected.full_name || playerSelected.email}
                    </div>
                    <div className="text-[11px] text-emerald-700 truncate">
                      {playerSelected.email}
                      {playerSelected.profile_key && (
                        <span className="ms-2 text-emerald-600">
                          · {playerSelected.profile_key}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearPlayer}
                  className="text-emerald-700 hover:text-emerald-900 shrink-0"
                  aria-label={t("racketLab.tickets.createPlayerClear")}
                  data-testid="racket-lab-tickets-create-player-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="ticket-player-id"
                    value={playerQuery}
                    onChange={(e) => {
                      setPlayerQuery(e.target.value);
                      setPlayerOpen(true);
                    }}
                    onFocus={() => setPlayerOpen(true)}
                    placeholder={t("racketLab.tickets.createPlayerPlaceholder")}
                    autoComplete="off"
                    className="ps-8"
                    data-testid="racket-lab-tickets-create-player-search"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {t("racketLab.tickets.createPlayerHint")}
                </p>

                {playerOpen && playerQuery.trim().length >= 2 && (
                  <div
                    className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-64 overflow-y-auto"
                    data-testid="racket-lab-tickets-create-player-results"
                  >
                    {playerSearching ? (
                      <div className="px-3 py-2 text-xs text-slate-400">
                        {t("racketLab.tickets.createPlayerSearching")}
                      </div>
                    ) : playerSuggestions.length === 0 ? (
                      <div
                        className="px-3 py-2 text-xs text-rose-700"
                        data-testid="racket-lab-tickets-create-player-empty"
                      >
                        {t("racketLab.tickets.createPlayerNoResults")}
                      </div>
                    ) : (
                      playerSuggestions.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setPlayerSelected(p);
                            setPlayerOpen(false);
                            setPlayerQuery("");
                            setPlayerSuggestions([]);
                          }}
                          className="w-full text-start px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-b-0"
                          data-testid={`racket-lab-tickets-create-player-result-${p.id}`}
                        >
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {p.full_name || p.email}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {p.email}
                            {p.profile_key && (
                              <span className="ms-2 text-slate-400">
                                · {p.profile_key}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Racket model + Sport */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ticket-racket">
                {t("racketLab.tickets.fieldRacket")}{" "}
                <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="ticket-racket"
                value={racketModel}
                onChange={(e) => setRacketModel(e.target.value)}
                onBlur={() => setRacketTouched(true)}
                placeholder={t("racketLab.tickets.createRacketPlaceholder")}
                aria-invalid={racketError ? "true" : undefined}
                className={racketError ? "border-rose-300" : ""}
                data-testid="racket-lab-tickets-create-racket"
              />
              {racketError && (
                <p
                  className="text-[11px] text-rose-700 mt-1"
                  data-testid="racket-lab-tickets-create-racket-error"
                >
                  {t("racketLab.tickets.createMissingRacket")}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="ticket-sport">
                {t("racketLab.tickets.fieldSport")}{" "}
                <span className="text-rose-600">*</span>
              </Label>
              <Select value={sport} onValueChange={setSport}>
                <SelectTrigger
                  id="ticket-sport"
                  data-testid="racket-lab-tickets-create-sport"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SPORTS.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`racket-lab-tickets-create-sport-${s}`}
                    >
                      {t(`racketLab.tickets.sport_${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="ticket-notes">{t("racketLab.tickets.fieldNotes")}</Label>
            <Textarea
              id="ticket-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t("racketLab.tickets.createNotesPlaceholder")}
              data-testid="racket-lab-tickets-create-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="racket-lab-tickets-create-cancel"
          >
            {t("racketLab.tickets.createCancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
            data-testid="racket-lab-tickets-create-submit"
          >
            {submitting ? "…" : t("racketLab.tickets.createSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
